/**
 * 接口错误 → 可读中文。
 *
 * 三端（admin-platform / admin-tenant / miniapp）共用同一套错误词汇，内容需保持一致：
 *   - admin-tenant：本文件
 *   - admin-platform：admin-platform/src/api/error-message.ts（与本文件逐字一致）
 *   - miniapp：miniapp/src/utils/error-message.ts（适配 ApiError，词典同步）
 * 改动任一处务必同步另外两处（docs/工程手册.md 红线：全链路闭环对齐）。
 *
 * 翻译优先级：
 *   1. 后端机器码 data.error（如 'claw.insufficient_balance'）→ CODE_ZH / 前缀规则
 *   2. 后端 message 文案：已知英文 → 翻译；已是中文 → 原样返回
 *   3. NestJS 默认英文状态名（'Bad Request' 等）→ ENGLISH_ZH
 *   4. HTTP 状态码兜底 → STATUS_ZH
 *   5. 调用方 fallback / '操作失败'
 *
 * 落地方式：axios 响应拦截器（src/api/axios.ts）统一调用 resolveApiError 把
 * data.error / data.message / err.message 改写成中文，并把原始机器码保留到 data.error_code。
 * 需要按机器码做逻辑分支的页面请用 rawErrorCode(err) 读原始码。
 */

/** 后端机器码 → 中文。来源：nestjs-platform 抛出的 `error: '...'`。 */
const CODE_ZH: Record<string, string> = {
  'plan.miniapp_purchase_disabled': '该套餐仅供展示，暂不支持在线购买',
  // 企业身份编辑入口专用，其他端不消费这些新增错误码。
  'users.identity_profile_invalid': '请检查姓名、名片联系电话和资料填写格式',
  'users.identity_profile_conflict': '资料已变化，请重新加载后再保存',
  'users.identity_profile_ambiguous': '当前身份的名片关联异常，请联系管理员核对',
  // 微信内容安全（msgSecCheck）判定违规。文案**刻意不透违规类型** —— 微信审核 3.2
  // 整改要求原文：「仅需提示用户所发布内容含违规信息即可」。label 只进服务端日志。
  'content.risky': '内容含违规信息，请修改后重试',
  // 租户
  // 组织类租户名称唯一。后端 message 已带上撞到的那个名字，
  // 这里是拿不到 message 时的兜底文案。
  'tenant.name_taken': '已存在同名租户（含已禁用的），请核对是否重复创建',
  // 工作台 Tab 配置（E 端企业资料 / A 端租户管理 → 工作台Tab配置）
  'workbench_tab.duplicate_name': '存在重复的 Tab 名称，请改成不同的名称',
  'workbench_tab.duplicate_slot': '存在重复的槽位编号，请刷新后重试',
  'workbench_tab.invalid_viewer': '可见成员须为本企业在职员工',
  'tenant.closed': '已注销租户不可操作',
  // 订单 / 支付
  'order.not_payable': '订单已关闭或已支付，请刷新后重试',
  // 账号 / 鉴权
  account_disabled: '账号已被禁用，请联系管理员',
  'auth.permission_denied': '无权限执行此操作',
  'auth.role_insufficient': '当前角色权限不足',
  invalid_credentials: '账号或密码错误',
  'invalid credentials': '账号或密码错误',
  weak_password: '新密码强度不足',
  same_password: '新密码不能与原密码相同',
  invalid_code: '验证码错误或已过期',
  code_expired: '验证码已过期，请重新获取',
  invalid_captcha: '图形验证码错误',
  captcha_expired: '图形验证码已过期，请刷新',
  invalid_phone: '手机号格式有误',
  invalid_email: '邮箱格式有误',
  phone_not_registered: '该手机号未注册',
  phone_taken: '该手机号已被注册',
  duplicate_username: '该登录账号已存在',
  sms_send_failed: '验证码发送失败，请稍后再试',
  sms_rate_limited: '发送过于频繁，请稍后再试',
  // 角色 / 部门
  builtin_role_immutable: '内置角色不可修改',
  role_in_use: '该角色仍在使用中，无法删除',
  role_not_assignable: '该角色不可分配',
  cannot_demote_last_owner: '不能移除最后一位创始人',
  // 员工（ADR-34 认领成员 = 本人个人账号，企业端不可动其凭证/资料）
  'users.cannot_reset_claimed_member_password': '该员工使用本人的个人账号登录，企业管理员不能重置其密码',
  'users.cannot_edit_claimed_member_profile': '该员工使用本人的个人账号，企业端不能修改其个人资料；职位/部门请在席位花名册维护',
  'users.cannot_demote_owner': '不能修改创始人的角色',
  // 租户 / 席位
  tenant_not_found: '企业不存在',
  not_member_of_tenant: '你不属于该企业',
  no_seat: '暂无可用席位',
  // 钱包 / 计费
  'claw.insufficient_balance': '元力余额不足，请先充值',
  'claw.account_missing': '未找到账户信息',
  insufficient_balance: '余额不足，请先充值',
  amount_required: '请填写金额',
  // ADR-49 企业元力池
  'coin_pool.insufficient_balance': '元力池余额不足，请先充值或减少分配数量',
  'coin_pool.insufficient_for_even': '池余额不足以给每人分配至少 1 元力',
  'coin_pool.target_not_member': '所选成员中有人不是本企业在职成员，请刷新后重试',
  'coin_pool.target_frozen': '所选成员中有账号已被平台冻结，请取消勾选后重试',
  'coin_pool.duplicate_target': '同一成员不能重复分配',
  'coin_pool.no_recipients': '请至少勾选一名成员',
  'coin_pool.too_many_recipients': '单次最多分配 200 人，请分批操作',
  'coin_pool.allocation_not_found': '分配记录不存在',
  // 订单 / 发票
  'order.not_owned': '该订单不属于当前账号',
  'order.not_pending': '订单状态已变更，无法操作',
  'order.not_bank_transfer': '仅对公转账渠道的订单可人工确认到账',
  'order.recharge_target_not_member': '目标用户不是本企业在职成员',
  'order.target_user_not_allowed': '当前入口不支持指定充值对象',
  'order.payer_openid_missing': '缺少支付账号信息',
  'order.user_id_missing': '缺少用户信息',
  order_already_invoiced: '该订单已开过发票',
  order_not_invoiceable: '该订单不可开票',
  orders_not_issuable: '所选订单不可开票',
  order_ids_required: '请先选择订单',
  invalid_orders: '订单信息不合法',
  // 内容 / 公告 / 文件
  announcement_not_published: '公告尚未发布',
  announcement_published_immutable: '已发布的公告不可修改',
  company_brief_too_long: '公司简介内容过长',
  'competitor.limit_exceeded': '竞品数量已达上限',
  feedback_not_found: '反馈记录不存在',
  inbox_message_not_found: '消息不存在',
  file_required: '请先选择文件',
  file_too_large: '文件体积超出限制',
  invalid_file_type: '文件格式不支持',
  invalid_purpose: '用途参数不合法',
  invalid_id: '参数不合法',
  duplicate_value: '该值已存在，请勿重复',
  range_too_large: '查询范围过大，请缩小范围',
  // 待扣费
  'pending_charge.not_abandonable': '该扣费记录不可放弃',
  'pending_charge.not_found': '扣费记录不存在',
  'pending_charge.not_retryable': '该扣费记录不可重试',
  // 系统
  // GEO 故障码：三端词典保持一致。
  'cs.not_opened': '该组织尚未开通智能客服',
  'cs.personal_not_supported': '个人身份不支持智能客服',
  'cs.unavailable': '该组织暂不可用',
  'rag.index_not_bound': '尚未开通知识库，请联系平台',
  'geo.forbidden': '无权限执行此 GEO 操作',
  'geo.not_found': 'GEO 记录不存在或已删除',
  'geo.precondition': '当前条件不满足，请检查设置后重试',
  'geo.version_conflict': '数据已变化，请刷新后重试',
  'geo.source_invalid': '来源数据已变化或不可用，请刷新后重试',
  'geo.quota_exceeded': 'GEO 席位额度不足，请调整范围或联系平台管理员',
  'geo.result_unavailable': '历史任务结果暂不可查看',
  'geo.attachment_too_large': '附件过大，请缩小文件后重试',
  'geo.unsupported_attachment': '暂不支持此附件格式',
  'geo.attachment_parse_failed': '附件解析失败，请检查文件后重试',
  'geo.image_unreadable': '图片无法读取，请更换图片后重试',
  'geo.no_text': '附件中没有可提炼的文字',
  'geo.no_material': '当前没有可用于生成的素材',
  'geo.invalid_model_output': '模型结果格式异常，本次任务未完成',
  'geo.outcome_unknown': '模型调用结果不明，需核查后处理',
  'geo.usage_invalid': '模型用量数据异常，费用待核查',
  'geo.usage_missing': '模型用量数据异常，费用待核查',
  'geo.balance_negative': '扣费管理员元力余额不足',
  'geo.cleanup_failed': '资源清理失败，请稍后重试',
  'geo.task_failed': '任务执行失败，请查看任务记录',
  database_error: '数据处理失败，请稍后重试',
  internal_error: '服务器开小差了，请稍后重试',
};

/** NestJS 默认英文状态名 / 常见英文文案 → 中文。 */
const ENGLISH_ZH: Record<string, string> = {
  'Bad Request': '请求参数有误',
  Unauthorized: '登录已过期，请重新登录',
  Forbidden: '无权限执行此操作',
  'Forbidden resource': '无权限执行此操作',
  'Not Found': '请求的资源不存在',
  Conflict: '操作冲突，请刷新后重试',
  'Request Timeout': '请求超时，请稍后重试',
  'Payload Too Large': '上传内容过大',
  'Unsupported Media Type': '文件格式不支持',
  'Unprocessable Entity': '请求参数有误',
  'Too Many Requests': '操作过于频繁，请稍后再试',
  'Internal Server Error': '服务器开小差了，请稍后重试',
  'Bad Gateway': '服务暂时不可用，请稍后重试',
  'Service Unavailable': '服务暂时不可用，请稍后重试',
  'Gateway Timeout': '请求超时，请稍后重试',
};

/**
 * 后端抛出的英文 message 文案（非机器码）→ 中文。键统一小写。
 * 来源：nestjs-platform 各 *Exception('english ...') / message:'english'。
 */
const MESSAGE_ZH: Record<string, string> = {
  'a pending exchange already exists': '已有一个待处理的名片交换请求',
  'alert not found': '告警不存在',
  'already bound to a group': '已绑定集团',
  'already revoked': '已撤销，请勿重复操作',
  'audio session not found': '音频会话不存在',
  'bank transfer review not found': '银行转账审核记录不存在',
  'body tenant_id must match x-tenant-id header': '请求的企业与当前登录企业不一致',
  'cannot be own parent': '不能将自己设为上级',
  'cannot exchange with self': '不能与自己交换名片',
  'cannot save own card': '不能收藏自己的名片',
  'cannot un-hide force-hidden highlight (phase 3 admin only)': '该资讯已被平台强制下架，无法自行恢复',
  'card not found': '名片不存在',
  'card not found in trash': '回收站中未找到该名片',
  'card not found or not public': '名片不存在或未公开',
  'contact group not found': '联系人分组不存在',
  'cycle detected': '检测到循环层级，无法保存',
  'exchange expired': '该名片交换请求已过期',
  'exchange not found': '名片交换请求不存在',
  'group has no active group-plan': '集团没有生效的集团套餐',
  'group has no active group-plan subscription': '集团没有生效的集团套餐订阅',
  'group not found': '集团不存在',
  'highlight is not force-hidden': '该资讯未被强制下架',
  'highlight not found': '资讯不存在',
  'news.official_account_url_required': '关联公众号时请填写有效的公众号文章链接',
  'news.video_not_allowed': '企业资讯仅支持图文',
  'lead not found': '线索不存在',
  'merge_into_profile_id not found in scope': '目标合并档案不存在',
  'message not found': '消息不存在',
  'missing sub': '登录信息无效，请重新登录',
  'missing tenant context': '缺少企业信息，请重新登录',
  'missing tenant_id': '缺少企业信息',
  'missing tenant_id in jwt': '登录信息缺少企业，请重新登录',
  'no active relation': '没有生效的关联关系',
  'no active seat assignment': '没有生效的席位分配',
  'no changes': '没有需要保存的改动',
  'no seats available': '暂无可用席位',
  'no tenant context for seat assignment': '缺少企业信息，无法分配席位',
  'not a miniapp user role': '当前身份无小程序使用权限',
  'not a platform admin': '当前身份不是平台管理员',
  'not a tenant admin': '当前身份不是企业管理员',
  'not a tenant user': '当前身份不是企业用户',
  'not in collection': '该名片不在收藏中',
  'not in open state': '当前状态不可操作',
  'not in pending_dedupe state': '当前不在待去重状态',
  'not in pending_review state': '当前不在待审核状态',
  'not your application': '这不是你的申请',
  'not your exchange': '这不是你的交换请求',
  'not your grant': '这不是你的授权',
  'not your group': '这不是你的集团',
  'not your profile': '这不是你的档案',
  'not your relation': '这不是你的关联关系',
  'old password mismatch': '原密码错误',
  'only enterprise tenants can apply to groups': '仅企业可申请加入集团',
  'order not found': '订单不存在',
  'order belongs to a different user': '该订单属于其他用户',
  'parent must be same tenant': '上级必须属于同一企业',
  'parent_tenant_id must point to a group root': '上级必须是集团总部',
  'pending profile not found': '待处理档案不存在',
  'pending_match_id missing — cannot merge': '缺少匹配信息，无法合并',
  'plan not found': '套餐不存在',
  'platform admin only': '仅平台管理员可操作',
  'platform_default theme must not have target_tenant_id': '平台默认主题不能指定目标企业',
  'please create your card first': '请先创建你的名片',
  'reconcile only applies to bank_transfer orders': '对账仅适用于银行转账订单',
  'relation not found': '关联关系不存在',
  'saved card not found': '未找到已收藏的名片',
  'session has no segments to analyze': '会话没有可分析的内容',
  'session not found': '会话不存在',
  'source not found': '源记录不存在',
  'target card not found': '目标名片不存在',
  'tenant admin only': '仅企业管理员可操作',
  'tenant not found': '企业不存在',
  tenant_not_active: '企业未激活',
  'tenant_specific theme requires target_tenant_id': '企业专属主题需指定目标企业',
  'theme not found': '主题不存在',
  'todo not found': '待办不存在',
  'user already has seat in this enterprise': '该用户在本企业已有席位',
  'user not found': '用户不存在',
  'user not in this enterprise': '该用户不属于本企业',
  'x-tenant-id header required': '缺少企业标识',
  'analysis_id required': '缺少分析 ID',
  'id must be numeric': 'ID 必须为数字',
  'user has no wx_openid; complete miniapp wx-login first': '请先完成微信登录',
  'internal server error': '服务器开小差了，请稍后重试',
};

/** Prisma 唯一约束冲突字段 → 中文（配合 'duplicate <field>' 文案）。 */
const DUP_FIELD_ZH: Record<string, string> = {
  phone: '该手机号已被使用',
  username: '该登录账号已被使用',
  email: '该邮箱已被使用',
  credit_code: '该统一社会信用代码已被使用',
  name: '该名称已被使用',
};

/** HTTP 状态码兜底 → 中文。 */
const STATUS_ZH: Record<number, string> = {
  400: '请求参数有误',
  401: '登录已过期，请重新登录',
  403: '无权限执行此操作',
  404: '请求的资源不存在',
  408: '请求超时，请稍后重试',
  409: '操作冲突，请刷新后重试',
  413: '上传内容过大',
  415: '文件格式不支持',
  422: '请求参数有误',
  429: '操作过于频繁，请稍后再试',
  500: '服务器开小差了，请稍后重试',
  502: '服务暂时不可用，请稍后重试',
  503: '服务暂时不可用，请稍后重试',
  504: '请求超时，请稍后重试',
};

function hasCJK(s: string): boolean {
  return /[一-鿿]/.test(s);
}

/** 机器码 → 中文（含前缀规则：payment.wxpay* / internal.*）。 */
function translateCode(code: string | undefined): string | undefined {
  if (!code) return undefined;
  if (CODE_ZH[code]) return CODE_ZH[code];
  if (code.startsWith('payment.wxpay')) return '支付处理失败，请稍后重试';
  if (code.startsWith('internal.')) return '服务校验失败，请稍后重试';
  return undefined;
}

/**
 * 英文 message 文案 → 中文（精确表 + 动态规则）。命不中返回 undefined。
 * 动态规则覆盖后端拼接型文案：'duplicate <field>'、'... has duplicate options'、
 * 手机号格式校验、'at most N custom fields' 等。
 */
function translateMessage(raw: string): string | undefined {
  const msg = raw.trim();
  const lower = msg.toLowerCase();
  if (CODE_ZH[msg]) return CODE_ZH[msg]; // 偶有把机器码塞进 message
  if (MESSAGE_ZH[lower]) return MESSAGE_ZH[lower];

  const dup = lower.match(/^duplicate\s+([a-z_]+)/);
  if (dup) return DUP_FIELD_ZH[dup[1]] || '信息重复，请勿重复提交';
  if (/has duplicate options$/.test(lower)) return '该字段存在重复选项';
  if (/(e\.164 format|valid cn mobile number)/.test(lower) || /^(phone|sender_phone) must be/.test(lower))
    return '手机号格式不正确';
  const atMost = lower.match(/^at most (\d+) custom fields$/);
  if (atMost) return `最多只能添加 ${atMost[1]} 个自定义字段`;

  if (ENGLISH_ZH[msg]) return ENGLISH_ZH[msg];
  return undefined;
}

/** message 字段归一为字符串（class-validator 会给字符串数组）。 */
function normalizeMessage(msg: unknown): string | undefined {
  if (Array.isArray(msg)) {
    const parts = msg.filter((x): x is string => typeof x === 'string');
    return parts.length ? parts.join('；') : undefined;
  }
  return typeof msg === 'string' && msg ? msg : undefined;
}

/** 单条 class-validator 英文校验信息 → 中文（字段名保留）；未匹配则原样返回（仍比泛化有用）。 */
function humanizeValidationItem(s: string): string {
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^property (\w+) should not exist$/))) return `不接受字段「${m[1]}」`;
  if ((m = s.match(/^(\w+) must be a number.*/))) return `「${m[1]}」必须是数字`;
  if ((m = s.match(/^(\w+) must be an integer.*/i))) return `「${m[1]}」必须是整数`;
  if ((m = s.match(/^(\w+) must be a string$/))) return `「${m[1]}」必须是文本`;
  if ((m = s.match(/^(\w+) must be a boolean.*/))) return `「${m[1]}」必须是是/否`;
  if ((m = s.match(/^(\w+) should not be empty$/))) return `「${m[1]}」不能为空`;
  if ((m = s.match(/^(\w+) must not be less than (-?\d+(?:\.\d+)?)/))) return `「${m[1]}」不能小于 ${m[2]}`;
  if ((m = s.match(/^(\w+) must not be greater than (-?\d+(?:\.\d+)?)/))) return `「${m[1]}」不能大于 ${m[2]}`;
  if ((m = s.match(/^(\w+) must be a valid enum value$/))) return `「${m[1]}」取值不合法`;
  if ((m = s.match(/^(\w+) must be longer than or equal to (\d+)/))) return `「${m[1]}」长度不足 ${m[2]}`;
  if ((m = s.match(/^(\w+) must be shorter than or equal to (\d+)/))) return `「${m[1]}」超出长度上限 ${m[2]}`;
  if ((m = s.match(/^(\w+) must be a valid (?:ISO ?8601 )?date.*/i))) return `「${m[1]}」日期格式不正确`;
  return s;
}

/** 校验错误数组（NestJS ValidationPipe 的 message[]）→ 中文，分号连接。 */
function humanizeValidation(items: string[]): string {
  return items.map(humanizeValidationItem).join('；');
}

/**
 * 把任意 axios 错误解析成可读中文。
 * @param err axios 抛出的 error（或任意带 response 的对象）
 * @param fallback 全都命不中时的兜底文案
 */
export function resolveApiError(err: unknown, fallback?: string): string {
  const e = err as { response?: { status?: number; data?: unknown }; code?: string; message?: string };

  // 无响应：网络中断 / 超时 / 跨域等
  if (!e?.response) {
    const m = e?.message || '';
    if (e?.code === 'ECONNABORTED' || /timeout/i.test(m)) return '请求超时，请稍后重试';
    return '网络连接失败，请检查网络后重试';
  }

  const status = e.response.status ?? 0;
  const data = e.response.data as { error?: unknown; error_code?: unknown; message?: unknown } | string | undefined;

  // data 可能是字符串（如 nginx 直接吐的 HTML/文本）
  const dataObj = data && typeof data === 'object' ? data : undefined;
  const rawCode =
    dataObj && typeof dataObj.error_code === 'string'
      ? dataObj.error_code
      : dataObj && typeof dataObj.error === 'string'
        ? dataObj.error
        : undefined;

  // 1. 余额不足：带门槛时展示「约需 / 当前」，避免用户误以为有余额却被拦
  if (rawCode === 'claw.insufficient_balance' || rawCode === 'insufficient_balance') {
    const body = dataObj as { balance?: unknown; min_required?: unknown } | undefined;
    const need = Number(body?.min_required);
    if (Number.isFinite(need) && need > 0) {
      const balance = Number(body?.balance);
      const current = Number.isFinite(balance) ? Math.max(0, Math.floor(balance)) : 0;
      return `还需约${Math.floor(need)}元力，当前${current}`;
    }
    return '元力余额不足，请先充值';
  }

  // 2. 机器码
  const fromCode = translateCode(rawCode);
  if (fromCode) return fromCode;
  if (rawCode?.startsWith('geo.')) return 'GEO 操作未完成，请刷新后重试或联系管理员';

  // 2. 校验错误数组（class-validator 字段级原因）→ 优先展示并中文化，便于一眼定位是哪个字段错
  //    （否则 data.error='Bad Request' 会落到泛化「请求参数有误」，看不出原因）
  if (dataObj && Array.isArray((dataObj as { message?: unknown }).message)) {
    const items = (dataObj as { message: unknown[] }).message.filter((x): x is string => typeof x === 'string');
    if (items.length) return humanizeValidation(items);
  }

  // 2b. 单条 message 文案：精确/动态翻译 → 已是中文则原样 → 否则落状态码兜底
  const msg = normalizeMessage(dataObj?.message);
  if (msg) {
    const t = translateMessage(msg);
    if (t) return t;
    if (hasCJK(msg)) return msg; // 后端本就给了中文，直接用
    // 纯英文且未收录 → 落到状态码兜底
  }

  // 3. NestJS 英文状态名（data.error 常为 'Bad Request' 等）
  if (rawCode && ENGLISH_ZH[rawCode]) return ENGLISH_ZH[rawCode];

  // 4. HTTP 状态码兜底
  if (STATUS_ZH[status]) return STATUS_ZH[status];

  return fallback || '操作失败';
}

/**
 * 读取原始后端机器码（拦截器会把它保留到 data.error_code）。
 * 需要按码做逻辑分支的页面用这个，而不是已被改写成中文的 data.error。
 */
export function rawErrorCode(err: unknown): string | undefined {
  const data = (err as { response?: { data?: { error_code?: unknown; error?: unknown } } })?.response?.data;
  if (!data || typeof data !== 'object') return undefined;
  if (typeof data.error_code === 'string') return data.error_code;
  if (typeof data.error === 'string') return data.error;
  return undefined;
}
